<template>
  <div class="roomie-otp-input">
    <RoomieInputLabel
      v-if="label"
      :id="id"
      class="roomie-otp-input__label"
      :label="label"
      :required="required" />
    <div class="roomie-otp-input__container">
      <input
        v-for="(_, index) in size"
        :id="`${id}-digit-${index}`"
        :key="index"
        ref="digits"
        :class="[
          `roomie-otp-input__digit roomie-input__container ${status}`,
          disabled && 'disabled'
        ]"
        :autofocus="autofocus && index === 0"
        :inputmode="inputModeComputed"
        :disabled="disabled"
        :is-internal-validate="false"
        type="text"
        autocomplete="off"
        maxlength="1"
        :value="currentOtp[index]"
        @input="onInput"
        @keydown.delete="onDelete"
        @keydown="onKeyDown"
        @paste="onPaste">
    </div>
    <span
      v-show="helperText"
      :class="`roomie-otp-input__helper-text roomie-input__helper-text ${status}`">
      {{ helperText }}
    </span>
    <span
      v-if="timeLimitComputed"
      class="roomie-otp-input__timer">
      Tu código vence en <b class="timer__count-down">{{ timeLimitComputed }}</b></span>
  </div>
</template>

<script>
import RoomieInputLabel from '../RoomieInputLabel/index.vue'
import { STATUS as INPUT_STATUS } from '../RoomieInput/utils'

const KEYBOARD_KEYS = {
  BACKSPACE: 'Backspace',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  TAB: 'Tab',
  DELETE: 'Delete'
}

function isSticking(event) {
  return (event.ctrlKey || event.metaKey) && event.key === 'v'
}

export default {
  name: 'RoomieOtpInput',
  components: { RoomieInputLabel },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    helperText: {
      type: String,
      default: undefined
    },
    id: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: undefined,
    },
    modelValue: {
      type: String,
      default: undefined
    },
    required: {
      type: Boolean,
      default: false
    },
    size: {
      type: Number,
      default: 6
    },
    status: {
      type: String,
      default: INPUT_STATUS.default,
      validator: value => Object.values(INPUT_STATUS).includes(value)
    },
    value: {
      type: String,
      default: undefined
    },
    pattern: {
      type: String,
      default: '^[0-9]+$'
    },
    timeLimit: {
      type: Number,
      default: undefined
    },
    autofocus: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input', 'update:modelValue', 'filled', 'time-up'],
  data() {
    const initialValue = this.modelValue ?? this.value
    const validationRegExp = new RegExp(this.pattern)

    return {
      currentOtp: initialValue && validationRegExp.test(initialValue) ? initialValue?.split('') : [],
      unidentifiedKeyPressed: false,
      timeToReachLimit: this.timeLimit,
      interval: undefined,
      shouldMoveToPrevious: false
    }
  },
  computed: {
    timeLimitComputed() {
      if (!this.timeToReachLimit) return

      const minutes = String(Math.floor(this.timeToReachLimit / 60))
      const seconds = String(this.timeToReachLimit - minutes * 60)
      return `${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`
    },
    inputModeComputed(){
      const regExp = new RegExp(this.pattern)

      if (!regExp.test('abcdefghijklmnopqrstuvwxyx') && regExp.test('1234567890')){
        return 'tel'
      }

      return 'text'
    }
  },
  watch: {
    modelValue(newOtp, oldOtp) {
      if (newOtp.length > this.size) return 
      if (newOtp === oldOtp) return
    
      this.setCurrentOtp(newOtp)
    },
    value(newOtp, oldOtp) {
      if (newOtp.length > this.size) return 
      if (newOtp === oldOtp) return
      
      this.setCurrentOtp(newOtp)
    }
  },
  mounted() {
    this.startCountDown()
  },
  beforeDestroy() {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = undefined
    }
  },
  methods: {
    setCurrentOtp(value) {
      if (!value) {
        this.currentOtp = []
        return
      }

      const validationRegExp = new RegExp(this.pattern)
      if (!value || !validationRegExp.test(value)){
        return
      }
      
      this.currentOtp = value.split('')
    },
    emitValue() {
      const currentValue = this.currentOtp.join('')

      this.$emit('input', currentValue)
      this.$emit('update:modelValue', currentValue)

      if (currentValue.length === this.size) {
        this.$emit('filled', currentValue)
      }
    },
    focusPrevious(index) {
      const previousRef = this.$refs.digits[index - 1] ?? this.$refs.digits[0]

      previousRef?.focus()
      previousRef.setSelectionRange(1,1)
    },
    focusNext(index) {
      const nextRef = this.$refs.digits[index + 1] ?? this.$refs.digits[this.size - 1]
      nextRef?.focus()
    },
    onInput(event) {
      let currentInputIndex = Number(event.target.id?.replace(`${this.id}-digit-`, ''))
      const oldValue = this.currentOtp[currentInputIndex]
      this.currentOtp[currentInputIndex] = event.target.value
      
      if(!this.unidentifiedKeyPressed){
        this.focusNext(currentInputIndex)
        this.emitValue()
        return
      }
      
      //Android Backspace Hack
      this.unidentifiedKeyPressed = false
      const validationRegExp = new RegExp(this.pattern)

      if (validationRegExp.test(event.target.value)){
        this.focusNext(currentInputIndex)
        this.emitValue()
        return
      }
      
      if (event.target.value === '' && !!oldValue){
        this.focusPrevious(currentInputIndex)
      }
      
      this.currentOtp[currentInputIndex] = ''
      this.$forceUpdate()
      this.emitValue()
    },
    onPaste(event) {
      event.stopPropagation()
      const textToPaste = event.clipboardData.getData('text/plain')
      const validationRegExp = new RegExp(this.pattern)

      if (!textToPaste || textToPaste?.length > this.size || ! validationRegExp.test(textToPaste)) {
        return event.preventDefault()
      }

      this.currentOtp = textToPaste.split('')
      const inputToFocus = this.$refs.digits[this.currentOtp.length]
      inputToFocus?.focus?.()
      event.preventDefault()
      this.emitValue()
    },
    onDelete(event) {
      const currentInputIndex = Number(event.target.id?.replace(`${this.id}-digit-`, ''))

      this.shouldMoveToPrevious = true
      if (event.target.value !== undefined && event.target.value !== '') {
        this.currentOtp[currentInputIndex] = ''
        event.target.value = ''

        this.emitValue()
        this.shouldMoveToPrevious = false
        return true
      }

      for (let index = currentInputIndex; index < this.$refs.digits.length; index++) {
        const element = this.$refs.digits[index]
        if (element.value) {
          this.currentOtp[index] = ''
          element.value = ''
          this.emitValue()
          break
        }
      }

      const previousRef = this.$refs.digits[currentInputIndex - 1] ?? this.$refs.digits[0]
      previousRef.value = ''
      this.currentOtp[currentInputIndex - 1] = ''

      return true
    },
    onKeyDown(event) {
      const currentInputIndex = Number(event.target.id?.replace(`${this.id}-digit-`, ''))
      const allowedKeys = [KEYBOARD_KEYS.BACKSPACE, KEYBOARD_KEYS.ARROW_LEFT, KEYBOARD_KEYS.ARROW_RIGHT, KEYBOARD_KEYS.TAB, 'Unidentified']
      const validationRegExp = new RegExp(this.pattern)
      
      if (isSticking(event)) {
        return true
      }

      if (!allowedKeys.includes(event.key) && (!validationRegExp.test(event.key) || event.key?.length > 1)) {
        event.preventDefault()
        return false
      }

      switch (event.key) {
      case KEYBOARD_KEYS.BACKSPACE:
        if (this.shouldMoveToPrevious) {
          this.shouldMoveToPrevious = false
          this.focusPrevious(currentInputIndex)
        }
        event.preventDefault()
        return false
      case KEYBOARD_KEYS.ARROW_LEFT:
        event.preventDefault()
        this.focusPrevious(currentInputIndex)
        return true
      case KEYBOARD_KEYS.ARROW_RIGHT:
        event.preventDefault()
        this.focusNext(currentInputIndex)
        return true
      default:
        break
      }
      
      //Android Backspace Hack
      this.unidentifiedKeyPressed = event.key === 'Unidentified'

      if (!validationRegExp.test(event.key) || !this.currentOtp[currentInputIndex] || this.unidentifiedKeyPressed){
        return true
      }
      
      this.currentOtp[currentInputIndex] = event.key
      this.$forceUpdate()
      this.focusNext(currentInputIndex)
      this.emitValue()
      event.preventDefault()
      return false
    },
    startCountDown() {
      if (this.timeLimit) {
        this.interval = setInterval(() => {
          this.timeToReachLimit--
          if (this.timeToReachLimit <= 0) {
            this.$emit('time-up')
            clearInterval(this.interval)
            this.interval = undefined
          }
        }, 1000)
      }
    },
    resetCountDown() {
      if (this.interval) {
        clearInterval(this.interval)
        this.interval = undefined
      }
      
      this.timeToReachLimit = this.timeLimit
      this.startCountDown()
    },
    focus(digit = 0) {
      this.$refs.digits[digit]?.focus()
    }
  },
  
}
</script>

<style src="./_index.scss" lang="scss"/>